import { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  CheckCircle2,
  CircleDot,
  AlertCircle,
  Circle,
  AlertTriangle,
  ChevronRight,
  ArrowUpDown,
} from 'lucide-react';
import type { TeamMember, SortingState } from '@/types';
import { getTeamMembers } from '@/utils/taskUtils';
import { getInitials, getAvatarColor } from '@/utils/formatters';
import { getTeamSortingOptions, sortItems, createDefaultSortingState } from '@/utils/grouping';
import { ViewToolbar, FilterPill, MultiSortingSelector } from '@/components/shared/view-toolbar';
import { WorkloadHeatMap } from '@/components/shared/WorkloadHeatMap';
import clsx from 'clsx';

import { useViewData } from '@/hooks/useViewData';
import { useAppStore } from '@/store/appStore';
import { useOrgMembers } from '@/hooks/useOrgMembers';
import { useProfilesContext } from '@/context/ProfilesContext';

type TeamFilter = 'all' | 'active' | 'overdue' | 'idle';

export function TeamView() {
  const {
    enrichedTasks,
    sorting,
    onSortingChange,
  } = useViewData();
  const navigate = useNavigate();

  const { orgMembers, isCurrentOrgMember, refetchMembers, loaded: orgLoaded } = useOrgMembers();
  const { resolveDisplayName, getProfile } = useProfilesContext();
  const [localSorting, setLocalSorting] = useState<SortingState>(() =>
    sorting || createDefaultSortingState()
  );
  const [teamFilter, setTeamFilter] = useState<TeamFilter>('all');

  const allMembers = useMemo(() => {
    return getTeamMembers(enrichedTasks, resolveDisplayName);
  }, [enrichedTasks, resolveDisplayName]);

  // Current org members (with zero-task members injected)
  const currentMembers = useMemo(() => {
    if (!orgLoaded) return [];
    const members = allMembers.filter(m => isCurrentOrgMember(m.userId));
    const existingIds = new Set(members.map(m => m.userId));
    for (const om of orgMembers) {
      if (!existingIds.has(om.user_id)) {
        members.push({
          userId: om.user_id,
          name: resolveDisplayName(om.user_id),
          tasks: [],
          totalTasks: 0,
          completedTasks: 0,
          inProgressTasks: 0,
          blockedTasks: 0,
          openTasks: 0,
          overdueTasks: 0,
          totalTimeEstimated: 0,
        });
      }
    }
    return members;
  }, [allMembers, orgLoaded, orgMembers, isCurrentOrgMember, resolveDisplayName]);

  const pastMembers = useMemo(() => {
    return allMembers.filter(m => !isCurrentOrgMember(m.userId));
  }, [allMembers, isCurrentOrgMember]);

  // Apply sorting with new fields
  const sortMembers = useCallback((members: TeamMember[]) => {
    return sortItems(members, localSorting, (a, b, field, _order) => {
      switch (field) {
        case 'tasks':
          return a.totalTasks - b.totalTasks;
        case 'workload':
          return (a.inProgressTasks + a.openTasks) - (b.inProgressTasks + b.openTasks);
        case 'overdue':
          return a.overdueTasks - b.overdueTasks;
        case 'name':
          return a.name.localeCompare(b.name);
        case 'completion': {
          const aRate = a.totalTasks > 0 ? a.completedTasks / a.totalTasks : 0;
          const bRate = b.totalTasks > 0 ? b.completedTasks / b.totalTasks : 0;
          return aRate - bRate;
        }
        default:
          return 0;
      }
    });
  }, [localSorting]);

  const [searchQuery, setSearchQuery] = useState('');

  // Apply team filter
  const filteredByTeamFilter = useMemo(() => {
    switch (teamFilter) {
      case 'active':
        return currentMembers.filter(m => m.inProgressTasks > 0 || m.openTasks > 0);
      case 'overdue':
        return currentMembers.filter(m => m.overdueTasks > 0);
      case 'idle':
        return currentMembers.filter(m => m.totalTasks === 0 || (m.inProgressTasks === 0 && m.openTasks === 0));
      default:
        return currentMembers;
    }
  }, [currentMembers, teamFilter]);

  const teamMembersSorted = useMemo(() => sortMembers(filteredByTeamFilter), [filteredByTeamFilter, sortMembers]);

  const teamMembers = useMemo(() => {
    if (!searchQuery.trim()) return teamMembersSorted;
    const q = searchQuery.toLowerCase();
    return teamMembersSorted.filter(m => m.name.toLowerCase().includes(q));
  }, [teamMembersSorted, searchQuery]);

  const [activeMembersCollapsed, setActiveMembersCollapsed] = useState(false);
  const [pastMembersCollapsed, setPastMembersCollapsed] = useState(true);
  const sortedPastMembers = useMemo(() => sortMembers(pastMembers), [pastMembers, sortMembers]);

  const stats = useMemo(() => {
    const totalMembers = currentMembers.length;
    const totalOverdue = currentMembers.reduce((sum, m) => sum + m.overdueTasks, 0);
    const activeMembers = currentMembers.filter(m => m.inProgressTasks > 0 || m.openTasks > 0).length;
    return { totalMembers, totalOverdue, activeMembers };
  }, [currentMembers]);

  const handleMemberClick = (member: TeamMember) => {
    navigate(`/profile/${encodeURIComponent(member.userId)}`);
  };

  // Sorting handlers
  const handleSortingChange = useCallback((newSorting: SortingState) => {
    setLocalSorting(newSorting);
    onSortingChange(newSorting);
  }, [onSortingChange]);

  return (
    <div className="flex-1 flex overflow-hidden animate-fade-in">
      {/* Team List */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <ViewToolbar
          onOpenSidebar={() => useAppStore.getState().setMobileSidebarOpen(true)}
          icon={<Users className="w-4 h-4 text-accent-blue" />}
          title="Team"
          subtitle={`${stats.totalMembers} membri`}
          leftContent={
            <div className="flex items-center gap-2 gap-y-2 flex-wrap">
              {/* Search by name */}
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cerca membro..."
                  className="h-7 w-40 pl-7 pr-2 rounded-lg text-[11px] bg-surface-secondary border border-border-primary text-text-primary placeholder:text-text-quaternary focus:outline-none focus:border-accent-violet/50"
                />
                <Users className="w-3 h-3 text-text-quaternary absolute left-2 top-1/2 -translate-y-1/2" />
              </div>
              <MultiSortingSelector
                sorting={localSorting}
                onSortingChange={handleSortingChange}
                availableOptions={getTeamSortingOptions()}
                icon={<ArrowUpDown className="w-3.5 h-3.5 text-text-tertiary" />}
              />
            </div>
          }
        />

        {/* Team Filters */}
        <div className="flex gap-1.5 px-4 py-2 border-b border-border-primary bg-surface-tertiary/20 flex-wrap">
          <FilterPill
            icon={<Users className="w-3 h-3" />}
            label="Tutti"
            count={currentMembers.length}
            isActive={teamFilter === 'all'}
            onClick={() => setTeamFilter('all')}
            size="sm"
          />
          <FilterPill
            icon={<CircleDot className="w-3 h-3" />}
            label="Attivi"
            count={stats.activeMembers}
            isActive={teamFilter === 'active'}
            onClick={() => setTeamFilter('active')}
            color="blue"
            size="sm"
          />
          <FilterPill
            icon={<AlertTriangle className="w-3 h-3" />}
            label="Con scaduti"
            count={currentMembers.filter(m => m.overdueTasks > 0).length}
            isActive={teamFilter === 'overdue'}
            onClick={() => setTeamFilter('overdue')}
            color="rose"
            size="sm"
          />
          <FilterPill
            icon={<Circle className="w-3 h-3" />}
            label="Inattivi"
            count={currentMembers.filter(m => m.totalTasks === 0 || (m.inProgressTasks === 0 && m.openTasks === 0)).length}
            isActive={teamFilter === 'idle'}
            onClick={() => setTeamFilter('idle')}
            color="default"
            size="sm"
          />
        </div>

        {/* Members List */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {/* Membri attivi accordion */}
            <div>
              <button
                onClick={() => setActiveMembersCollapsed(!activeMembersCollapsed)}
                className="flex items-center gap-2 hover:bg-surface-hover px-2 py-1.5 rounded-md transition-colors w-full"
              >
                <ChevronRight
                  className={clsx(
                    'w-3.5 h-3.5 text-text-quaternary transition-transform',
                    !activeMembersCollapsed && 'rotate-90'
                  )}
                />
                <span className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">
                  Membri attivi
                </span>
                <span className="text-[10px] text-text-quaternary tabular-nums font-medium">
                  {teamMembers.length}
                </span>
              </button>
              {!activeMembersCollapsed && (
                teamMembers.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mt-3">
                    {teamMembers.map((member) => (
                      <MemberCard
                        key={member.userId}
                        member={member}
                        onClick={() => handleMemberClick(member)}
                        avatarUrl={getProfile(member.userId)?.avatar_url}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-text-quaternary text-[12px]">
                    Nessun membro trovato
                  </div>
                )
              )}
            </div>

            {/* Membri passati accordion */}
            {sortedPastMembers.length > 0 && (
              <div className="pt-2 border-t border-border-primary">
                <button
                  onClick={() => { refetchMembers(); setPastMembersCollapsed(!pastMembersCollapsed); }}
                  className="flex items-center gap-2 hover:bg-surface-hover px-2 py-1.5 rounded-md transition-colors w-full"
                >
                  <ChevronRight
                    className={clsx(
                      'w-3.5 h-3.5 text-text-quaternary transition-transform',
                      !pastMembersCollapsed && 'rotate-90'
                    )}
                  />
                  <span className="text-[11px] font-semibold text-accent-amber uppercase tracking-wide">
                    Membri passati
                  </span>
                  <span className="text-[10px] text-text-quaternary tabular-nums font-medium">
                    {sortedPastMembers.length}
                  </span>
                </button>
                {!pastMembersCollapsed && (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mt-3">
                    {sortedPastMembers.map((member) => (
                      <MemberCard
                        key={member.userId}
                        member={member}
                        onClick={() => handleMemberClick(member)}
                        avatarUrl={getProfile(member.userId)?.avatar_url}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}


interface MemberCardProps {
  member: TeamMember;
  onClick: () => void;
  avatarUrl?: string | null;
}

function MemberCard({ member, onClick, avatarUrl }: MemberCardProps) {
  const completionRate = member.totalTasks > 0
    ? Math.round((member.completedTasks / member.totalTasks) * 100)
    : 0;

  return (
    <div
      onClick={onClick}
      className="p-4 rounded-xl border cursor-pointer transition-colors group bg-surface-primary border-border-primary hover:border-border-secondary hover:shadow-lg"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        {avatarUrl ? (
          <img src={avatarUrl} alt={member.name} className="w-10 h-10 rounded-full object-cover shadow-lg" />
        ) : (
          <div className={clsx(
            'w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white shadow-lg',
            getAvatarColor(member.name)
          )}>
            {getInitials(member.name)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="text-[13px] font-semibold text-text-primary truncate group-hover:text-accent-violet transition-colors">
            {member.name}
          </h3>
          <p className="text-[10px] text-text-quaternary">{member.totalTasks} task assegnati</p>
        </div>
        <ChevronRight className="w-4 h-4 text-text-quaternary opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[10px] mb-1">
          <span className="text-text-quaternary uppercase tracking-wider font-medium">Completamento</span>
          <span className="text-text-secondary font-semibold tabular-nums">{completionRate}%</span>
        </div>
        <div className="h-1.5 bg-surface-tertiary rounded-full overflow-hidden">
          <div
            className="h-full bg-accent-emerald rounded-full transition-all"
            style={{ width: `${completionRate}%` }}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2">
        <MiniStat icon={<CheckCircle2 className="w-3 h-3" />} value={member.completedTasks} color="emerald" />
        <MiniStat icon={<CircleDot className="w-3 h-3" />} value={member.inProgressTasks} color="blue" />
        <MiniStat icon={<AlertCircle className="w-3 h-3" />} value={member.blockedTasks} color="rose" />
        <MiniStat icon={<Circle className="w-3 h-3" />} value={member.openTasks} color="gray" />
      </div>

      {/* Weekly workload heatmap */}
      <div className="mt-3 pt-3 border-t border-border-primary/50">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] text-text-quaternary uppercase tracking-wider font-medium">Carico settimanale</span>
        </div>
        <WorkloadHeatMap tasks={member.tasks} days={7} compact />
      </div>

      {/* Overdue Warning */}
      {member.overdueTasks > 0 && (
        <div className="mt-2 flex items-center gap-1.5 px-2 py-1 rounded-md bg-accent-rose/10">
          <AlertTriangle className="w-3 h-3 text-accent-rose" />
          <span className="text-[10px] text-accent-rose font-medium">{member.overdueTasks} task in ritardo</span>
        </div>
      )}
    </div>
  );
}

interface MiniStatProps {
  icon: React.ReactNode;
  value: number;
  color: 'emerald' | 'blue' | 'rose' | 'gray';
}

function MiniStat({ icon, value, color }: MiniStatProps) {
  const colorClasses = {
    emerald: 'text-accent-emerald',
    blue: 'text-accent-blue',
    rose: 'text-accent-rose',
    gray: 'text-text-quaternary',
  };

  return (
    <div className="flex items-center gap-1">
      <span className={colorClasses[color]}>{icon}</span>
      <span className="text-[11px] font-semibold text-text-secondary tabular-nums">{value}</span>
    </div>
  );
}

